import {
  Alert,
  Button,
  Card,
  Descriptions,
  Empty,
  Form,
  Input,
  InputNumber,
  Modal,
  Segmented,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd';
import { observer } from 'mobx-react-lite';
import { useEffect, useRef, useState } from 'react';
import { ADMIN_PERMISSIONS } from '@/auth/permissions';
import {
  type CapacityUserRooms,
  type RoomCapacity,
  roomCapacityApi,
} from '@/services/roomCapacityApi';
import { authStore } from '@/stores';
import { capacityRoomId, capacityUserId } from './query';

function errorMessage(error: unknown): string {
  const response = (
    error as {
      response?: {
        status?: number;
        data?: { message?: string; reason?: string };
      };
    }
  )?.response;
  if (response?.status === 403)
    return '当前账号没有执行此操作的权限，请联系管理员。';
  if (response?.status === 404) return '未找到对应的用户或房间。';
  if (response?.status === 409)
    return '配置已变更或正在发布，请刷新房间信息后重试。';
  return (
    response?.data?.message ||
    (error instanceof Error ? error.message : '操作失败，请稍后重试。')
  );
}

type PendingExpansion = {
  roomId: string;
  previousMaxMembers: number;
  maxMembers: number;
  versionNumber: number;
};

function expansionApplied(
  room: RoomCapacity,
  pending: PendingExpansion,
): boolean {
  return (
    room.currentVersion >= pending.versionNumber &&
    room.maxMembers >= pending.maxMembers &&
    room.deploymentState === 'COMPLETE'
  );
}

export const RoomCapacityPage = observer(() => {
  const canWrite = authStore.can(ADMIN_PERMISSIONS.roomCapacityWrite);
  const [kind, setKind] = useState<'room' | 'user'>('room');
  const [input, setInput] = useState('');
  const [user, setUser] = useState<CapacityUserRooms | null>(null);
  const [room, setRoom] = useState<RoomCapacity | null>(null);
  const [relation, setRelation] = useState('all');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [pendingExpansion, setPendingExpansion] =
    useState<PendingExpansion | null>(null);
  const [editing, setEditing] = useState<RoomCapacity | null>(null);
  const [form] = Form.useForm<{ maxMembers: number; reason: string }>();
  const generation = useRef(0);
  const submitting = useRef(false);
  const pending =
    pendingExpansion?.roomId === room?.room.roomId ? pendingExpansion : null;
  const pendingRoomId =
    room && (pending || !room.canExpand) && !error && !loading
      ? room.room.roomId
      : '';
  useEffect(
    () => () => {
      generation.current++;
    },
    [],
  );
  useEffect(() => {
    if (!canWrite) setEditing(null);
  }, [canWrite]);
  useEffect(() => {
    if (!pendingRoomId) return;
    const request = generation.current;
    let stopped = false;
    let timer = 0;
    const poll = async () => {
      try {
        const result = await roomCapacityApi.room(pendingRoomId);
        if (stopped || request !== generation.current) return;
        setRoom(result);
        if (pending ? expansionApplied(result, pending) : result.canExpand) {
          setPendingExpansion(null);
          setNotice('房间配额配置已生效。');
        } else {
          timer = window.setTimeout(() => void poll(), 3000);
        }
      } catch (cause) {
        if (!stopped && request === generation.current) {
          setError(`自动刷新失败：${errorMessage(cause)} 请点击刷新重试。`);
        }
      }
    };
    timer = window.setTimeout(() => void poll(), 3000);
    return () => {
      stopped = true;
      window.clearTimeout(timer);
    };
  }, [pendingRoomId, pending]);

  const lookupRoom = async (roomId: string) => {
    const request = ++generation.current;
    const submitted =
      pendingExpansion?.roomId === roomId ? pendingExpansion : null;
    setPendingExpansion(submitted);
    setLoading(true);
    setError('');
    setNotice('');
    setRoom(null);
    setEditing(null);
    try {
      const result = await roomCapacityApi.room(roomId);
      if (request === generation.current) {
        setRoom(result);
        if (submitted && expansionApplied(result, submitted)) {
          setPendingExpansion(null);
          setNotice('房间配额配置已生效。');
        }
      }
    } catch (cause) {
      if (request === generation.current) setError(errorMessage(cause));
    } finally {
      if (request === generation.current) setLoading(false);
    }
  };
  const lookupUser = async (userId: string, filter = 'all', cursor = '') => {
    const request = ++generation.current;
    setLoading(true);
    setError('');
    setNotice('');
    setPendingExpansion(null);
    setRoom(null);
    setEditing(null);
    if (!cursor) setUser(null);
    try {
      const result = await roomCapacityApi.user(userId, filter, cursor);
      if (request === generation.current) {
        setRelation(filter);
        setUser((previous) =>
          cursor && previous
            ? { ...result, rooms: [...previous.rooms, ...result.rooms] }
            : result,
        );
      }
    } catch (cause) {
      if (request === generation.current) setError(errorMessage(cause));
    } finally {
      if (request === generation.current) setLoading(false);
    }
  };
  const search = () => {
    try {
      setUser(null);
      if (kind === 'room') void lookupRoom(capacityRoomId(input));
      else void lookupUser(capacityUserId(input));
    } catch (cause) {
      generation.current++;
      setLoading(false);
      setRoom(null);
      setPendingExpansion(null);
      setError(errorMessage(cause));
    }
  };
  const expand = async () => {
    if (!editing || !canWrite || submitting.current) return;
    const values = await form.validateFields().catch(() => null);
    if (
      !values ||
      submitting.current ||
      !Number.isInteger(values.maxMembers) ||
      values.maxMembers <= editing.maxMembers ||
      !values.reason.trim()
    )
      return;
    const selected = editing;
    const request = generation.current;
    let submitted: PendingExpansion | null = null;
    submitting.current = true;
    setSaving(true);
    setError('');
    setNotice('');
    try {
      const result = await roomCapacityApi.expand(selected.room.roomId, {
        maxMembers: values.maxMembers,
        expectedVersion: selected.currentVersion,
        reason: values.reason.trim(),
      });
      if (request !== generation.current) return;
      submitted = {
        roomId: selected.room.roomId,
        previousMaxMembers: selected.maxMembers,
        maxMembers: values.maxMembers,
        versionNumber: result.versionNumber,
      };
      const refreshed = await roomCapacityApi.room(selected.room.roomId);
      if (request !== generation.current) return;
      setRoom(refreshed);
      if (expansionApplied(refreshed, submitted)) {
        setPendingExpansion(null);
        setNotice('房间配额配置已生效。');
      } else {
        setPendingExpansion(submitted);
      }
    } catch (cause) {
      if (request !== generation.current) return;
      setPendingExpansion(submitted);
      setError(
        submitted
          ? `扩容已提交（目标 ${submitted.maxMembers} 人），但读取最新配额失败：${errorMessage(cause)} 请点击刷新核对，无需再次提交。`
          : errorMessage(cause),
      );
    } finally {
      submitting.current = false;
      if (request === generation.current) {
        setEditing(null);
        setSaving(false);
      }
    }
  };
  return (
    <section className="space-y-6" aria-label="房间配额管理">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <Typography.Title level={3}>房间配额管理</Typography.Title>
          <Typography.Text type="secondary">
            查询用户的房间数量，按房间 ID
            或分享链接查看和扩容人数上限。人数包含房主。
          </Typography.Text>
        </div>
        <Tag color={canWrite ? 'blue' : undefined}>
          {canWrite ? '可查询与扩容' : '只读'}
        </Tag>
      </div>
      <Card>
        <Form layout="inline" onFinish={search}>
          <Form.Item>
            <Segmented
              value={kind}
              options={[
                { label: '按房间查询', value: 'room' },
                { label: '按用户查询', value: 'user' },
              ]}
              onChange={(value) => {
                setKind(value as 'room' | 'user');
                setInput('');
              }}
            />
          </Form.Item>
          <Form.Item className="min-w-64 flex-1">
            <Input
              aria-label={kind === 'room' ? '房间 ID 或链接' : '用户 UID'}
              placeholder={
                kind === 'room'
                  ? '粘贴房间 ID、分享链接或房间地址'
                  : '输入数字 UID'
              }
              value={input}
              onChange={(event) => setInput(event.target.value)}
              allowClear
              disabled={saving}
            />
          </Form.Item>
          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              disabled={saving}
            >
              查询
            </Button>
          </Form.Item>
        </Form>
      </Card>
      {error && <Alert showIcon type="error" title={error} />}
      {pending ? (
        <Alert
          showIcon
          type="info"
          title={`扩容已提交：${pending.previousMaxMembers} → ${pending.maxMembers} 人，正在核对最新配额。`}
        />
      ) : (
        notice && <Alert showIcon type="success" title={notice} />
      )}
      {user && (
        <Card
          title={`${user.user.displayName || user.user.userId} 的房间`}
          extra={
            <Tag>
              {user.roomCount}
              {user.roomCountCapped ? '+' : ''} 个房间
            </Tag>
          }
        >
          <Space orientation="vertical" size="middle" className="w-full">
            <Typography.Text type="secondary">
              UID {user.user.userId} ·
              房间总数包含创建和加入的房间，不代表可创建房间的数量上限。
            </Typography.Text>
            <Segmented
              value={relation}
              disabled={loading || saving}
              options={[
                { label: '全部房间', value: 'all' },
                { label: '创建的房间', value: 'owner' },
                { label: '加入的房间', value: 'member' },
              ]}
              onChange={(value) =>
                void lookupUser(user.user.userId, String(value))
              }
            />
            <Table
              rowKey={(item) => item.room.roomId}
              pagination={false}
              loading={loading}
              dataSource={user.rooms}
              columns={[
                {
                  title: '房间',
                  render: (_, item) => (
                    <div>
                      <div>{item.room.name || '未命名房间'}</div>
                      <Typography.Text type="secondary" copyable>
                        {item.room.roomId}
                      </Typography.Text>
                    </div>
                  ),
                },
                {
                  title: '身份',
                  dataIndex: 'role',
                  render: (value) => (value === 'owner' ? '房主' : '成员'),
                },
                {
                  title: '房主',
                  render: (_, item) =>
                    item.room.owner.displayName || item.room.owner.userId,
                },
                {
                  title: '操作',
                  render: (_, item) => (
                    <Button
                      type="link"
                      disabled={loading || saving}
                      onClick={() => void lookupRoom(item.room.roomId)}
                    >
                      查看配额
                    </Button>
                  ),
                },
              ]}
            />
            {user.nextCursor && (
              <Button
                loading={loading}
                disabled={saving}
                onClick={() =>
                  void lookupUser(user.user.userId, relation, user.nextCursor)
                }
              >
                加载更多房间
              </Button>
            )}
          </Space>
        </Card>
      )}
      {room && (
        <Card
          title={room.room.name || '未命名房间'}
          extra={
            <Space>
              <Button
                disabled={loading || saving}
                onClick={() => void lookupRoom(room.room.roomId)}
              >
                刷新
              </Button>
              {canWrite && (
                <Button
                  type="primary"
                  disabled={
                    !room.canExpand ||
                    !!pending ||
                    !!error ||
                    loading ||
                    saving ||
                    room.maxMembers >= 1000000
                  }
                  onClick={() => {
                    form.resetFields();
                    form.setFieldsValue({
                      maxMembers: Math.min(
                        1000000,
                        Math.max(10, room.maxMembers + 1),
                      ),
                    });
                    setEditing(room);
                  }}
                >
                  扩容
                </Button>
              )}
            </Space>
          }
        >
          <Descriptions
            column={{ xs: 1, sm: 2 }}
            items={[
              {
                key: 'room',
                label: '房间 ID',
                children: (
                  <Typography.Text copyable>{room.room.roomId}</Typography.Text>
                ),
              },
              {
                key: 'owner',
                label: '房主',
                children: `${room.room.owner.displayName || '未命名用户'}（${room.room.owner.userId}）`,
              },
              {
                key: 'count',
                label: '当前成员',
                children: `${room.memberCount}${room.memberCountCapped ? '+' : ''} 人`,
              },
              {
                key: 'capacity',
                label: '人数上限（含房主）',
                children: pending
                  ? `${pending.previousMaxMembers} → ${pending.maxMembers} 人（同步中）`
                  : `${room.maxMembers} 人`,
              },
              {
                key: 'source',
                label: '配额来源',
                children: room.overridden
                  ? '此房间单独设置'
                  : `默认配额（${room.defaultMaxMembers} 人）`,
              },
              {
                key: 'status',
                label: '配置状态',
                children: pending ? (
                  <Tag color="blue">
                    正在同步（目标 {pending.maxMembers} 人）
                  </Tag>
                ) : error ? (
                  <Tag color="orange">待刷新核对</Tag>
                ) : room.canExpand ? (
                  <Tag color="green">已生效</Tag>
                ) : (
                  <Tag color="orange">待发布完成或待核对</Tag>
                ),
              },
            ]}
          />
          {!room.canExpand && !pending && !error && (
            <Alert
              className="mt-4"
              showIcon
              type="warning"
              title="当前存在待完成的配置变更，暂时无法继续扩容。发布完成后将自动更新；也可点击刷新核对状态。"
            />
          )}
        </Card>
      )}
      {!user && !room && !loading && !error && (
        <Empty description="输入用户 UID 或房间 ID 开始查询" />
      )}
      <Modal
        title="扩容房间"
        open={!!editing && canWrite}
        confirmLoading={saving}
        closable={!saving}
        maskClosable={!saving}
        cancelButtonProps={{ disabled: saving }}
        onCancel={() => {
          if (!saving) setEditing(null);
        }}
        onOk={() => void expand()}
        okText="确认扩容"
      >
        <Typography.Paragraph>
          {editing?.room.name || editing?.room.roomId}：当前上限{' '}
          {editing?.maxMembers} 人（含房主）。
        </Typography.Paragraph>
        <Form form={form} layout="vertical" preserve={false}>
          <Form.Item
            name="maxMembers"
            label="新人数上限"
            rules={[
              { required: true, message: '请输入新人数上限' },
              {
                type: 'integer',
                min: (editing?.maxMembers || 1) + 1,
                max: 1000000,
                message: '新人数上限必须大于当前上限，且不超过 1000000',
              },
            ]}
          >
            <InputNumber
              className="w-full"
              min={(editing?.maxMembers || 1) + 1}
              max={1000000}
              precision={0}
            />
          </Form.Item>
          <Form.Item
            name="reason"
            label="扩容原因"
            rules={[
              { required: true, whitespace: true, message: '请填写扩容原因' },
              { max: 300, message: '原因不能超过 300 字' },
            ]}
          >
            <Input.TextArea rows={3} maxLength={300} showCount />
          </Form.Item>
        </Form>
      </Modal>
    </section>
  );
});
